// ============================================================
// THE FIGURE MODEL - what the display draws for one combatant (plan
// map-display, Track B). One family for everyone (ruling 2026-10-07): a ring
// on the ground, a stalk, a head. What changes is read here, from the live
// projection and the sheet, and nothing is stored:
//
//   - the CLASS picks the head's plain polyhedron: a sphere for the living, a
//     cube for a tank, a hexagonal prism held high for a walker, a small low
//     cube for a bike, a skimmer's cube on a dashed stalk; an amorphous head
//     will not hold its shape.
//   - the POSTURE family: standing, prone, helpless, dead (the "DOWN" body).
//   - the MOVEMENT TRAIT: flyer, hoverer, burrower, incorporeal (phasing
//     among them: it passes through terrain as they do).
//   - whether it is a CROWD: a horde keeps its bodies and its ring.
//   - the drawn DIAMETER: the sheet's "Token size", when set.
//
// Pure. Zero React dependency.
// ============================================================
import { isCrowd, resolveBodyShape } from '../bodyShape';
import { condType } from '../conditions';
import { vehicleKindOf, type VehicleKind } from '../vehicleKind';
import { traitIdsOf } from '../battlefield/traitRules';
import { drawnDiameterOf, terrainBoxOf } from '../objects/objectSpec';

export type FigureClass = 'living' | 'amorphous' | 'object' | VehicleKind;
type Posture = 'stand' | 'prone' | 'helpless' | 'dead';
type MoveTrait = 'flyer' | 'hoverer' | 'burrower' | 'incorporeal';

export interface FigureModel {
  klass: FigureClass;
  posture: Posture;
  trait: MoveTrait | null;
  /** A horde rather than one body: it keeps its crowd and its ring, never a lying capsule. */
  crowd: boolean;
  /** The sheet's drawn diameter in metres, or null to draw at the footprint. */
  diameter: number | null;
  /** How high above its ground the head rests, metres: an Object's diamond on top of its box (2026-10-26), else 0. */
  perch?: number;
  /** A terrain Object's box (w along its heading, d across): its ring runs round this footprint (board 6). */
  box?: { w: number; d: number } | null;
}

/** What the model reads of the live projection (a Target). */
interface FigureTarget {
  bodyType?: string;
  conditions?: readonly string[];
  horde?: { isHorde?: boolean; magnitude?: number };
  incorporeal?: boolean;
  tokenDiameter?: number;
  vehicleKind?: VehicleKind;
}

/** What it reads of the sheet: its traits. Absent for a statblock without one. */
interface FigureSheet {
  traits?: { traitId?: string }[];
  /** An Object's own fields: its box, when it counts as terrain. */
  bodyType?: string;
  objectSpec?: unknown;
}

/** The posture family, worst first: a dead body lies, whatever else it is. */
const POSTURES: readonly [Posture, readonly string[]][] = [
  ['dead', ['K']],
  ['helpless', ['U', 'Hpl']],
  ['prone', ['P']],
];

/** The movement traits the figure shows, the one that wins first. */
const TRAITS: readonly MoveTrait[] = ['flyer', 'hoverer', 'burrower'];

function classOf(target: FigureTarget): FigureClass {
  const shape = resolveBodyShape(target);
  if (shape === 'vehicle') return vehicleKindOf(target);
  if (shape === 'object') return 'object';
  return shape === 'amorphous' ? 'amorphous' : 'living';
}

function postureOf(target: FigureTarget): Posture {
  const held = new Set((target.conditions ?? []).map(condType));
  return POSTURES.find(([, codes]) => codes.some((c) => held.has(c)))?.[0] ?? 'stand';
}

function traitOf(target: FigureTarget, sheet: FigureSheet | null, klass: FigureClass): MoveTrait | null {
  const has = new Set(traitIdsOf(sheet));
  if (target.incorporeal || has.has('phase')) return 'incorporeal';
  // A skimmer hovers by what it is, with no trait to say so.
  return TRAITS.find((t) => has.has(t)) ?? (klass === 'skimmer' ? 'hoverer' : null);
}

/** The figure for one combatant: its live projection and, where there is one, its sheet. */
export function figureModel(target: FigureTarget, sheet: FigureSheet | null): FigureModel {
  const klass = classOf(target);
  // An Object that counts as terrain (2026-10-26): its ring encircles its box, its diamond rests on top.
  const box = klass === 'object' ? terrainBoxOf(sheet) : null;
  return {
    klass,
    posture: postureOf(target),
    trait: traitOf(target, sheet, klass),
    crowd: isCrowd(target),
    diameter: drawnDiameterOf(target.tokenDiameter),
    perch: box?.h ?? 0,
    box: box ? { w: box.w, d: box.d } : null,
  };
}
